import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import NoSqlAttributesEditor from "./nosql-attributes-editor";
import {
  NoSqlOntologyField,
  useCreateNoSqlField,
  useUpdateNoSqlField,
} from "@/service-hooks/use-get-nosql-ontology-service";

interface NoSqlFieldDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  collectionId: string;
  collectionName?: string;
  existing?: NoSqlOntologyField;
  onSaved?: () => void;
}

interface FormState {
  name: string;
  fullPath: string;
  parentPath: string;
  dataType: string;
  nullable: boolean;
  description: string;
  attributes: string;
}

const EMPTY: FormState = {
  name: "",
  fullPath: "",
  parentPath: "",
  dataType: "",
  nullable: true,
  description: "",
  attributes: "",
};

export default function NoSqlFieldDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  collectionId,
  collectionName,
  existing,
  onSaved,
}: NoSqlFieldDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createField = useCreateNoSqlField();
  const updateField = useUpdateNoSqlField();
  const isEdit = !!existing?.id;
  const isPending = createField.isPending || updateField.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: existing?.name ?? "",
      fullPath: existing?.fullPath ?? "",
      parentPath: existing?.parentPath ?? "",
      dataType: existing?.dataType ?? "",
      nullable: existing?.nullable !== false,
      description: existing?.description ?? "",
      attributes: existing?.attributes ?? "",
    });
  }, [isOpen, existing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!isEdit && (!form.name.trim() || !form.fullPath.trim())) return;
    if (!form.dataType.trim()) return;
    try {
      if (isEdit && existing?.id) {
        // name / fullPath / parentPath are immutable server-side.
        await updateField.mutateAsync({
          id: existing.id,
          projectUuid,
          dataLakeId,
          data: {
            dataType: form.dataType.trim(),
            nullable: form.nullable,
            description: form.description.trim() || undefined,
            attributes: form.attributes || undefined,
          },
        });
      } else {
        await createField.mutateAsync({
          collectionId,
          projectUuid,
          dataLakeId,
          data: {
            name: form.name.trim(),
            fullPath: form.fullPath.trim(),
            parentPath: form.parentPath.trim() || undefined,
            dataType: form.dataType.trim(),
            nullable: form.nullable,
            description: form.description.trim() || undefined,
            attributes: form.attributes || undefined,
          },
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  const canSubmit =
    !!form.dataType.trim() &&
    (isEdit || (!!form.name.trim() && !!form.fullPath.trim()));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Field" : "Add Field"}
            {collectionName ? ` — ${collectionName}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the field fields for the selected NoSQL collection.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Name <span className="text-error">*</span>
              </label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. email"
                disabled={isEdit}
                className="bg-surface text-white disabled:opacity-60"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Full path <span className="text-error">*</span>
              </label>
              <Input
                value={form.fullPath}
                onChange={(e) => update("fullPath", e.target.value)}
                placeholder="e.g. profile.email, name.given"
                disabled={isEdit}
                className="bg-surface text-white font-mono disabled:opacity-60"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Data type <span className="text-error">*</span>
              </label>
              <Input
                value={form.dataType}
                onChange={(e) => update("dataType", e.target.value)}
                placeholder="e.g. String, Object, Array, ObjectId, S"
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Parent path</label>
              <Input
                value={form.parentPath}
                onChange={(e) => update("parentPath", e.target.value)}
                placeholder="Parent field path for nested/embedded (optional)"
                disabled={isEdit}
                className="bg-surface text-white font-mono disabled:opacity-60"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded border border-surface-border bg-surface px-3 py-2">
            <div>
              <div className="text-sm font-medium text-white">Nullable</div>
              <div className="text-xs text-muted">
                Off = required (e.g. Mongoose <code>required: true</code>).
              </div>
            </div>
            <Switch
              checked={form.nullable}
              onCheckedChange={(v) => update("nullable", v)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="What this field represents (optional)"
              className="bg-surface text-white min-h-[60px]"
            />
          </div>

          <NoSqlAttributesEditor
            value={form.attributes}
            onChange={(json) => update("attributes", json)}
            disabled={isPending}
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
